import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api } from "@/lib/api";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "FOLLOW_UP", "WON", "LOST"];
const SOURCES = ["manual", "scan", "event", "public", "import", "referral"];

// Owner-scoped manual lead creation. Reused by Leads.jsx and EventDetail.jsx.
// eventId (optional): pre-associates the lead with an event and lets the backend
// dedupe against an existing lead (same email/phone in workspace) and link instead
// of duplicating.
export function LeadFormDialog({ open, onClose, onCreated, eventId = null, cardSlug = null, defaultSource = "manual" }) {
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [f, setF] = useState({
    card_slug: cardSlug || "",
    name: "", email: "", phone: "", company: "", title: "",
    notes: "", source: defaultSource, status: "NEW", tags: "",
    next_follow_up: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/admin/cards").then(({ data }) => {
      const list = (data || []).filter((c) => c.status === "published" || c.status === "active");
      setCards(list);
      if (!f.card_slug && list[0]) setF((s) => ({ ...s, card_slug: list[0].slug }));
    }).catch(() => {});
    // Reset when opened fresh
    setF((s) => ({ ...s, name: "", email: "", phone: "", company: "", title: "", notes: "", tags: "", next_follow_up: "", status: "NEW", source: defaultSource }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.email.trim() && !f.phone.trim()) { toast.error("Email or phone is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        tags: f.tags ? f.tags.split(",").map((x) => x.trim()).filter(Boolean) : [],
        next_follow_up: f.next_follow_up || null,
        event_id: eventId || null,
      };
      const { data } = await api.post("/crm/leads", payload);
      toast.success(data.linked ? `Linked to existing lead: ${data.lead.name}` : "Lead created");
      onCreated?.(data.lead);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create lead");
    } finally { setBusy(false); }
  };

  const field = (key, placeholder, opts = {}) => (
    <input
      value={f[key]}
      onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none focus:border-[#D6A653]/70 focus:ring-1 focus:ring-[#D6A653]/40"
      data-testid={`lead-form-${key}`}
      {...opts}
    />
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl bg-[#0A0B0F] text-white border border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-light tracking-tight">
            <UserPlus className="h-4 w-4 text-[#D6A653]" /> Add lead {eventId ? "to this event" : "manually"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field("name", "Full name *")}
            {field("company", "Company")}
            {field("email", "Email", { type: "email" })}
            {field("phone", "Phone")}
            {field("title", "Job title")}
            {cards.length > 1 && (
              <Select value={f.card_slug} onValueChange={(v) => setF((s) => ({ ...s, card_slug: v }))}>
                <SelectTrigger className="rounded-lg border border-white/10 bg-black/40 text-sm text-white/90" data-testid="lead-form-card">
                  <SelectValue placeholder="Card" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0B0F] text-white/90 border-white/10">
                  {cards.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.slug}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
              <SelectTrigger className="rounded-lg border border-white/10 bg-black/40 text-sm text-white/90" data-testid="lead-form-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0B0F] text-white/90 border-white/10">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.source} onValueChange={(v) => setF((s) => ({ ...s, source: v }))}>
              <SelectTrigger className="rounded-lg border border-white/10 bg-black/40 text-sm text-white/90" data-testid="lead-form-source">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0B0F] text-white/90 border-white/10">
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              type="datetime-local"
              value={f.next_follow_up}
              onChange={(e) => setF((s) => ({ ...s, next_follow_up: e.target.value }))}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none focus:border-[#D6A653]/70"
              data-testid="lead-form-followup"
            />
          </div>
          {field("tags", "Tags (comma-separated)")}
          <textarea
            value={f.notes}
            onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))}
            placeholder="Notes"
            rows={3}
            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none focus:border-[#D6A653]/70"
            data-testid="lead-form-notes"
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onClose?.()} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5" data-testid="lead-form-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:brightness-110 disabled:opacity-50" data-testid="lead-form-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save lead
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
